import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { FaArrowDown, FaArrowRight, FaArrowUp, FaExchangeAlt, FaPiggyBank } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import axiosInstance from 'Services/APIService';
import {
  getInvestorLandingData,
  getLandingData,
  selectInvestorLandingData,
  selectInvestorLandingDataStatus,
  selectLandingData,
  selectLandingDataStatus,
  selectLoading
} from '../../../slices/fundManagementInvestment/fundmanagementInvestmentSlice';
import SellGrowthUnits from './SellGrowthUnits';
import SellIncomeUnits from './SellIncomeUnits';

interface UnitData {
  id: string;
  name: string;
  totalUnits: number;
  unitPrice: number;
  totalValue: number;
  description: string;
  type: 'incomeUnits' | 'growthUnits';
  gradient: string;
  icon: React.ReactNode;
  available: boolean;
  features: string[];
  riskLevel: string;
}

const SellUnits: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redux state
  const investorLandingData = useSelector(selectInvestorLandingData);
  const investorLandingDataStatus = useSelector(selectInvestorLandingDataStatus);
  const landingData = useSelector(selectLandingData);
  const landingDataStatus = useSelector(selectLandingDataStatus);
  const loading = useSelector(selectLoading);

  // Selected subscription state - toggle between cards and form
  const [selectedSubscription, setSelectedSubscription] = useState<
    'incomeUnits' | 'growthUnits' | null
  >(null);

  // Project info
  const selectedProjectId = useAppSelector(
    (state) => state.homeTilesServices.selectedProjectId || ''
  );

  // Get logged-in user data
  const [loggedInUser, setLoggedInUser] = useState(() => {
    const loginInfo = localStorage.getItem('user-details');
    if (loginInfo) {
      try {
        const userDetails = JSON.parse(loginInfo);
        return {
          name: userDetails.name || '',
          email: userDetails.email || '',
          avatar: userDetails.avatar || ''
        };
      } catch (error) {
        console.error('Error parsing user details:', error);
        return { name: '', email: '', avatar: '' };
      }
    }
    return { name: '', email: '', avatar: '' };
  });

  const [loggedUserAvatarUrl, setLoggedUserAvatarUrl] = useState<string | null>(null);

  // Get groupId from localStorage
  const getGroupId = () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
    };
    return selectedProject?.projectId || '';
  };

  // Load investor and admin landing data
  useEffect(() => {
    const groupId = getGroupId();
    if (groupId && loggedInUser.email) {
      dispatch(
        getInvestorLandingData({
          groupId: groupId,
          serviceId: 'fundManagementInvestment',
          investorsEmailId: loggedInUser.email
        }) as any
      );

      dispatch(
        getLandingData({
          groupId: groupId,
          serviceId: 'fundManagementInvestment',
          isAdmin: true
        }) as any
      );
    }
  }, [dispatch, loggedInUser.email]);

  // Fetch logged-in user avatar
  useEffect(() => {
    const fetchLoggedUserAvatar = async () => {
      if (loggedInUser.avatar) {
        try {
          const response = await axiosInstance.get(
            `/avatar/get-avatar?path=${encodeURIComponent(loggedInUser.avatar)}`,
            { responseType: 'blob' }
          );
          const imageUrl = URL.createObjectURL(response.data);
          setLoggedUserAvatarUrl(imageUrl);
        } catch (error) {
          console.error(`Error fetching logged user avatar:`, error);
          setLoggedUserAvatarUrl(null);
        }
      }
    };

    fetchLoggedUserAvatar();

    return () => {
      if (loggedUserAvatarUrl) {
        URL.revokeObjectURL(loggedUserAvatarUrl);
      }
    };
  }, [loggedInUser.avatar]);

  // Process unit data from APIs
  const unitData = useMemo((): UnitData[] => {
    const units: UnitData[] = [];

    const adminUnitPrices: { [key: string]: number } = {};

    if (landingDataStatus === 'succeeded' && landingData && Array.isArray(landingData)) {
      landingData.forEach((unit) => {
        if (unit && unit.unitType && unit.unitPrice) {
          const unitType = unit.unitType;
          adminUnitPrices[unitType] = parseFloat(unit.unitPrice) || 1.0;
        }
      });
    }

    if (
      investorLandingDataStatus === 'succeeded' &&
      investorLandingData &&
      investorLandingData.incomeUnits
    ) {
      const incomeData = investorLandingData.incomeUnits;
      const totalUnits = parseFloat(incomeData.totalUnitsHoldTillDate) || 0;
      const unitPrice = adminUnitPrices['incomeUnits'] || 1.0;
      const totalValue = totalUnits * unitPrice;

      units.push({
        id: 'income',
        name: 'Income Units',
        totalUnits,
        unitPrice,
        totalValue,
        description: 'Sell your income units to receive cash from your conservative investment',
        type: 'incomeUnits',
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        icon: <FaPiggyBank size={isMobile ? 28 : 32} />,
        available: totalUnits > 0,
        features: [
          'Regular quarterly distributions',
          'Lower volatility',
          'Stable income stream',
          'Conservative investment approach'
        ],
        riskLevel: 'Conservative'
      });
    }

    if (
      investorLandingDataStatus === 'succeeded' &&
      investorLandingData &&
      investorLandingData.growthUnits
    ) {
      const growthData = investorLandingData.growthUnits;
      const totalUnits = parseFloat(growthData.totalUnitsHoldTillDate) || 0;
      const unitPrice = adminUnitPrices['growthUnits'] || 1.0;
      const totalValue = totalUnits * unitPrice;

      units.push({
        id: 'growth',
        name: 'Growth Units',
        totalUnits,
        unitPrice,
        totalValue,
        description: 'Sell your growth units to liquidate your long-term capital investment',
        type: 'growthUnits',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        icon: <FaArrowUp size={isMobile ? 28 : 32} />,
        available: totalUnits > 0,
        features: [
          'Capital growth focus',
          'Higher growth potential',
          'Reinvested earnings',
          'Long-term wealth building'
        ],
        riskLevel: 'Moderate to High'
      });
    }

    return units;
  }, [investorLandingData, investorLandingDataStatus, landingData, landingDataStatus, isMobile]);

  const formatCurrency = (amount: number) => {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '$0';
    }

    const absAmount = Math.abs(amount);
    const isNegative = amount < 0;

    let formattedAmount;

    if (absAmount >= 1000000000) {
      formattedAmount = (absAmount / 1000000000).toFixed(3) + 'B';
    } else if (absAmount >= 1000000) {
      formattedAmount = (absAmount / 1000000).toFixed(3) + 'M';
    } else if (absAmount >= 1000) {
      formattedAmount = (absAmount / 1000).toFixed(3) + 'K';
    } else {
      formattedAmount = absAmount % 1 === 0 ? absAmount.toString() : absAmount.toFixed(3);
    }

    if (formattedAmount.includes('.')) {
      formattedAmount = formattedAmount.replace(/\.?0+$/, '');
    }

    const sign = isNegative ? '-' : '';
    return `${sign}$${formattedAmount}`;
  };

  const getRiskColor = (riskLevel: string) => {
    if (riskLevel.includes('Conservative')) return '#4caf50';
    if (riskLevel.includes('Moderate')) return '#ff9800';
    return '#f44336';
  };

  const handleCardClick = (unitType: 'incomeUnits' | 'growthUnits', available: boolean) => {
    if (!available) return;
    setSelectedSubscription(unitType);
  };

  const handleBackToCards = () => {
    setSelectedSubscription(null);
  };

  // Show loading state
  if (
    loading ||
    investorLandingDataStatus === 'idle' ||
    investorLandingDataStatus === 'pending' ||
    landingDataStatus === 'idle' ||
    landingDataStatus === 'pending'
  ) {
    return (
      <Container
        maxWidth="xl"
        sx={{
          padding: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary">
            Loading your portfolio data...
          </Typography>
        </Box>
      </Container>
    );
  }

  const totalPortfolioValue = unitData.reduce((sum, unit) => sum + unit.totalValue, 0);

  // If a subscription is selected, show the corresponding form
  if (selectedSubscription === 'incomeUnits') {
    return <SellIncomeUnits onBack={handleBackToCards} />;
  }

  if (selectedSubscription === 'growthUnits') {
    return <SellGrowthUnits onBack={handleBackToCards} />;
  }

  // Otherwise show the cards selection view
  return (
    <Container
      maxWidth="xl"
      sx={{
        padding: { xs: 1, sm: 2, md: 3 },
        minHeight: '100vh'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: { xs: 2, md: 3 },
          flexWrap: 'wrap',
          gap: { xs: 1, md: 0 }
        }}
      >
        <FaExchangeAlt
          size={isMobile ? 20 : 24}
          color="#d32f2f"
          style={{ marginRight: isMobile ? 8 : 12, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            component="h1"
            sx={{
              fontWeight: 500,
              color: '#333',
              fontSize: { xs: '1rem', md: '1.2rem' }
            }}
          >
            Sell Units
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              marginTop: 0.5,
              fontSize: { xs: '0.75rem', md: '0.8rem' }
            }}
          >
            Request to sell your income or growth units from the trust fund
          </Typography>
        </Box>
      </Box>

      {/* Unit Class Selection */}
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          marginBottom: { xs: 2, md: 2.5 },
          color: '#333',
          fontSize: { xs: '0.9rem', md: '1rem' }
        }}
      >
        Choose Your Unit Type to Sell
      </Typography>

      <Grid container spacing={{ xs: 2, md: 4 }}>
        {unitData.map((unitClass) => (
          <Grid item xs={12} md={6} key={unitClass.id}>
            <Card
              sx={{
                height: '100%',
                cursor: unitClass.available ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                borderRadius: { xs: 2, md: 3 },
                border: '2px solid #e0e0e0',
                position: 'relative',
                overflow: 'hidden',
                opacity: unitClass.available ? 1 : 0.6,
                '&:hover': unitClass.available
                  ? {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                      border: '2px solid #1976d2'
                    }
                  : {}
              }}
              onClick={() => handleCardClick(unitClass.type, unitClass.available)}
            >
              {!unitClass.available && (
                <Chip
                  label="No Units"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    backgroundColor: 'rgba(158, 158, 158, 0.3)',
                    color: '#666',
                    fontWeight: 500,
                    fontSize: '0.6rem',
                    height: 20,
                    zIndex: 1
                  }}
                />
              )}

              <CardActionArea disabled={!unitClass.available} sx={{ height: '100%' }}>
                {/* Header Gradient */}
                <Box
                  sx={{
                    background: unitClass.gradient,
                    padding: { xs: 2, md: 3 },
                    color: 'white'
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: '0.95rem', md: '1.1rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {unitClass.name}
                        </Typography>
                        <Chip
                          label="Live"
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(76, 175, 80, 0.3)',
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '0.6rem',
                            height: 20
                          }}
                        />
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '1.1rem', md: '1.3rem' }
                        }}
                      >
                        {formatCurrency(unitClass.unitPrice)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.9,
                          fontSize: { xs: '0.7rem', md: '0.75rem' }
                        }}
                      >
                        per unit
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'center', flexShrink: 0, marginLeft: 1 }}>
                      {unitClass.icon}
                    </Box>
                  </Box>
                </Box>

                <CardContent sx={{ padding: { xs: 2, md: 3 } }}>
                  <Typography
                    variant="body1"
                    sx={{
                      marginBottom: 2,
                      color: '#666',
                      lineHeight: 1.6,
                      fontSize: { xs: '0.8rem', md: '0.85rem' }
                    }}
                  >
                    {unitClass.description}
                  </Typography>

                  {/* Key Metrics */}
                  <Grid container spacing={{ xs: 1, md: 2 }} sx={{ marginBottom: 3 }}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          textAlign: 'center',
                          padding: { xs: 1.5, md: 2 },
                          backgroundColor: '#f5f5f5',
                          borderRadius: 2
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: '#333',
                            fontSize: { xs: '0.8rem', md: '0.9rem' }
                          }}
                        >
                          {unitClass.totalUnits.toLocaleString()}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontSize: { xs: '0.65rem', md: '0.7rem' } }}
                        >
                          Total Units
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box
                        sx={{
                          textAlign: 'center',
                          padding: { xs: 1.5, md: 2 },
                          backgroundColor: '#f5f5f5',
                          borderRadius: 2
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: '#333',
                            fontSize: { xs: '0.8rem', md: '0.9rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {formatCurrency(unitClass.totalValue)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontSize: { xs: '0.65rem', md: '0.7rem' } }}
                        >
                          Total Value
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Features */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      marginBottom: 2,
                      color: '#333',
                      fontSize: { xs: '0.75rem', md: '0.8rem' }
                    }}
                  >
                    Key Features
                  </Typography>

                  <Box sx={{ marginBottom: 3 }}>
                    {unitClass.features.map((feature, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          color: '#666',
                          marginBottom: 0.5,
                          fontSize: { xs: '0.7rem', md: '0.75rem' }
                        }}
                      >
                        • {feature}
                      </Typography>
                    ))}
                  </Box>

                  {/* Risk Level and Minimum Investment */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 2,
                      flexWrap: 'wrap',
                      gap: 1
                    }}
                  >
                    <Chip
                      label={`Risk: ${unitClass.riskLevel}`}
                      size="small"
                      sx={{
                        backgroundColor: getRiskColor(unitClass.riskLevel),
                        color: 'white',
                        fontWeight: 500,
                        fontSize: { xs: '0.6rem', md: '0.65rem' }
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: '#666', fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                    >
                      Available: {unitClass.totalUnits.toFixed(2)} units
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<FaArrowRight />}
                    disabled={!unitClass.available || Boolean(loading)}
                    sx={{
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0'
                      },
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: 2,
                      padding: { xs: '10px', md: '12px' },
                      fontSize: { xs: '0.8rem', md: '0.85rem' }
                    }}
                  >
                    {unitClass.available ? `Sell ${unitClass.name}` : 'No Units Available'}
                  </Button>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No Units Message */}
      {unitData.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
            No Units Available to Sell
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#999' }}>
            You don't have any units in your portfolio. Purchase units first to start selling.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/fundManagementInvestment/purchase')}
            sx={{ textTransform: 'none' }}
          >
            Purchase Units
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default SellUnits;
